/**
 * JobAttachments — upload + lista de anexos.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  FileText,
  Image as ImageIcon,
  Loader2,
  Paperclip,
  Trash2,
  Upload,
} from "lucide-react";
import { useState, type ChangeEvent, type DragEvent } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { jobAttachmentsApi, type JobAttachment } from "@/lib/api-job-extras";
import { formatRelative } from "@/lib/format";
import { cn } from "@/lib/utils";

export function JobAttachments({ jobId }: { jobId: string }) {
  const qc = useQueryClient();
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  const list = useQuery({
    queryKey: ["job-attachments", jobId],
    queryFn: () => jobAttachmentsApi.list(jobId),
    enabled: !!jobId,
  });

  const handleFiles = async (files: FileList | File[]) => {
    setUploading(true);
    for (const file of Array.from(files)) {
      try {
        const { upload_url, storage_path } = await jobAttachmentsApi.getUploadUrl(jobId, {
          filename: file.name,
          content_type: file.type || "application/octet-stream",
          size_bytes: file.size,
        });
        if (upload_url.startsWith("https://localhost")) {
          // stub bucket — só registra metadata
          toast.warning("UPLOADS_BUCKET não configurado em backend; registrando só metadata.");
        } else {
          const r = await fetch(upload_url, {
            method: "PUT",
            headers: { "Content-Type": file.type || "application/octet-stream" },
            body: file,
          });
          if (!r.ok) {
            const text = await r.text();
            throw new Error(`Upload GCS falhou (${r.status}): ${text.slice(0, 200)}`);
          }
        }
        await jobAttachmentsApi.register(jobId, {
          filename: file.name,
          content_type: file.type || "application/octet-stream",
          size_bytes: file.size,
          storage_path,
        });
        toast.success(`${file.name} enviado`);
      } catch (e) {
        toast.error(`Falha em ${file.name}`, {
          description: e instanceof Error ? e.message : "Erro",
        });
      }
    }
    setUploading(false);
    qc.invalidateQueries({ queryKey: ["job-attachments", jobId] });
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length > 0) {
      void handleFiles(e.dataTransfer.files);
    }
  };

  const onFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      void handleFiles(e.target.files);
      e.target.value = "";
    }
  };

  const remove = useMutation({
    mutationFn: (id: string) => jobAttachmentsApi.remove(jobId, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job-attachments", jobId] });
      toast.success("Removido");
    },
  });

  return (
    <div className="space-y-4">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={cn(
          "rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors",
          dragging
            ? "border-primary-500 bg-primary-50/40 dark:bg-primary-500/5"
            : "border-slate-300 dark:border-white/10 hover:border-slate-400 dark:hover:border-white/20",
        )}
        onClick={() => document.getElementById("attachment-file-input")?.click()}
      >
        <input
          id="attachment-file-input"
          type="file"
          multiple
          className="hidden"
          onChange={onFileInput}
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
            <p className="text-sm">Enviando…</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-50 text-primary-600 dark:bg-primary-500/10">
              <Upload className="h-5 w-5" />
            </div>
            <p className="text-sm font-medium">Clique ou arraste arquivos aqui</p>
            <p className="text-xs text-muted-foreground">
              Fotos, PDFs, comprovantes. Sem limite por enquanto.
            </p>
          </div>
        )}
      </div>

      {list.isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : list.data?.items.length === 0 ? (
        <EmptyState
          icon={Paperclip}
          title="Sem anexos"
          description="Faça upload de fotos do equipamento, comprovantes ou laudos."
        />
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {list.data?.items.map((att) => (
            <AttachmentCard key={att.id} att={att} onRemove={() => remove.mutate(att.id)} />
          ))}
        </ul>
      )}
    </div>
  );
}

function AttachmentCard({ att, onRemove }: { att: JobAttachment; onRemove: () => void }) {
  const isImage = att.content_type.startsWith("image/");
  const sizeKb = (att.size_bytes / 1024).toFixed(1);
  return (
    <li className="group relative rounded-xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 dark:bg-white/5 shrink-0">
          {isImage ? (
            <ImageIcon className="h-4 w-4 text-sky-600" />
          ) : (
            <FileText className="h-4 w-4 text-slate-600" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sm truncate" title={att.filename}>{att.filename}</p>
          <p className="text-xs text-muted-foreground">
            {sizeKb} KB · {formatRelative(att.created_at)}
          </p>
        </div>
      </div>
      <Button
        size="icon-sm"
        variant="ghost"
        onClick={onRemove}
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </li>
  );
}
